import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  TextField,
  MenuItem,
  Menu,
  IconButton,
  Typography,
  Card,
  Grid,
  LinearProgress,
  FormGroup,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Tweets from './Tweets';
import { useUser } from '../../AuthContext';
import { generateTweets } from '../../api/GenerateAPI';

const isValidYoutubeUrl = (url) => {
  const regex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[\w-]{11}(&\S*)?$/;
  return regex.test(url);
};

const Generate = () => {
  const [youtubeLink, setYoutubeLink] = useState('');
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [dropdown1, setDropdown1] = useState('one_liners');
  const [dropdown2, setDropdown2] = useState('insightful');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [tweets, setTweets] = useState([]);
  const [tweetsMetadata, setTweetsMetadata] = useState([]);
  const [anchorEl1, setAnchorEl1] = useState(null);
  const [anchorEl2, setAnchorEl2] = useState(null);
  const [progressTimer, setProgressTimer] = useState(null);
  const [totalTime, setTotalTime] = useState(0);
  
  // State for the advanced options toggle and checkbox selections
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [selectedFormats, setSelectedFormats] = useState([]);
  const { user, accessToken } = useUser();

  const allFeatures = ["profanity", "emojis", "quotes", "slang"];
  const allFormats = ["storytelling", "checklist", "conversation", "question"];

  const handleFeatureChange = (e) => {
    const { name, checked } = e.target;
    setSelectedFeatures(prev =>
      checked ? [...prev, name] : prev.filter(item => item !== name)
    );
  };

  const handleFormatChange = (e) => {
    const { name, checked } = e.target;
    setSelectedFormats(prev =>
      checked ? [...prev, name] : prev.filter(item => item !== name)
    );
  };

  const handleYoutubeLinkChange = (e) => {
    const value = e.target.value;
    setYoutubeLink(value);
    if (value && !isValidYoutubeUrl(value)) {
      setError("Invalid YouTube URL");
    } else {
      setError(null);
    }
  };

  const checkVideoDuration = (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      video.onerror = () => {
        reject(new Error('Error loading video metadata'));
      };
      video.src = URL.createObjectURL(file);
    });
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    try {
      const duration = await checkVideoDuration(selectedFile);
      if (duration > 1200) {
        setError('Video duration must be 20 minutes or less');
        setFile(null);
        return;
      }
      setError(null);
      setFile(selectedFile);
    } catch (error) {
      setError('Error checking video duration');
      setFile(null);
    }
  };

  const handleMenu1Click = (event) => {
    setAnchorEl1(event.currentTarget);
  };

  const handleMenu2Click = (event) => {
    setAnchorEl2(event.currentTarget);
  };

  const handleMenu1Close = () => {
    setAnchorEl1(null);
  };

  const handleMenu2Close = () => {
    setAnchorEl2(null);
  };

  const handleDropdown1Change = (value) => () => {
    setDropdown1(value);
    handleMenu1Close();
  };

  const handleDropdown2Change = (value) => () => {
    setDropdown2(value);
    handleMenu2Close();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file && (!youtubeLink || !isValidYoutubeUrl(youtubeLink))) {
      setError("Please provide a valid YouTube URL or upload a video file");
      return;
    }
    setError(null);
    setLoading(true);
    setProgress(0);
    setTweets([]);

    // Calculate total time based on input type
    let estimatedTime;
    if (file) {
      const duration = await checkVideoDuration(file);
      estimatedTime = (0.07667 * duration) + 8; // 0.07667 seconds per second of video + 5 seconds
    } else {
      estimatedTime = 16; // 10 seconds for YouTube links
    }
    setTotalTime(estimatedTime);

    // Start progress timer
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev < 95) {
          // Initial progress up to 95% at original speed
          const newProgress = prev + (100 / (estimatedTime * 10));
          return newProgress >= 95 ? 95 : newProgress;
        } else if (prev < 99) {
          // Slow progress from 95% to 99% (1% every 5 seconds)
          return prev + 0.2; // 0.2% every second (1% every 5 seconds)
        }
        return 99; // Pause at 99%
      });
    }, 100); // Back to 100ms for faster initial progress
    setProgressTimer(timer);

    const formData = new FormData();
    if (file) {
      formData.append('file', file);
      formData.append('content_type', 'video_upload');
    } else {
      formData.append('youtube_url', youtubeLink);
      formData.append('content_type', 'youtube_url');
    }
    
    allFeatures.forEach(f => {
      const value = showAdvanced && selectedFeatures.includes(f) ? 'true' : 'false';
      formData.append(`feature_${f}`, value);
    });
    allFormats.forEach(f => {
      const value = showAdvanced && selectedFormats.includes(f) ? 'true' : 'false';
      formData.append(`format_${f}`, value);
    });
    
    formData.append('tone', dropdown2);
    formData.append('type', dropdown1);

    try {
      const result = await generateTweets(formData, accessToken);
      clearInterval(progressTimer);
      setProgress(100);

      const tweetData = result.response;
      const tweetsFormatted = tweetData.tweets.map((tweet, idx) => ({
        avatarUrl: `https://freepnglogo.com/images/all_img/1725374683twitter-x-logo.png`,
        name: user.full_name,
        handle: user.full_name.toLowerCase().replace(/\s/g, ''),
        date: new Date().toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
        content: tweet.content.replace(/\n/g, '\n'),
        tags: tweet.tags,
      }));
      setTweets(tweetsFormatted);
      setTweetsMetadata(tweetData.metadata);
    } catch (error) {
      console.error('Error:', error);
      setError(error.message);
      clearInterval(progressTimer);
      setProgress(0);
    } finally {
      setLoading(false);
    }
  };

  // Cleanup timer on component unmount
  useEffect(() => {
    return () => {
      if (progressTimer) {
        clearInterval(progressTimer);
      }
    };
  }, [progressTimer]);

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>

      <Box display="flex" justifyContent="center" sx={{ mb: 2 }}>
      <Typography component="h2" variant="h4">
        Generate Tweets
      </Typography>
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center">
        <Card variant="outlined" sx={{ height: '100%', flexGrow: 1, maxWidth: "800px" }}>
          <form onSubmit={handleSubmit} encType="multipart/form-data">
            {/* Combined Input Row for YouTube Link and Video Upload */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TextField
                size="small"
                id="youtube-link"
                type="url"
                name="youtubeLink"
                placeholder="Insert YouTube Link"
                autoComplete="off"
                value={youtubeLink}
                onChange={handleYoutubeLinkChange}
                disabled={loading}
                variant="outlined"
                error={Boolean(error)}
                required={!file}
                sx={{ flex: 1, mr: 0.5 }}
              />
              <Typography variant="body2" sx={{ mx: 0.5 }}>or</Typography>
              <Button
      size="small"
      variant="outlined"
      color="primary"
      component="label"
      disabled={loading}
      sx={{ flex: 1, ml: 0.5 }}
    >
      {file ? <CheckCircleIcon sx={{ mr: 1, color: '#69db9c' }} /> : <FileUploadIcon sx={{ mr: 1 }} />}
      {file ? "File Uploaded" : "Upload Video File"}
      <input
        type="file"
        accept="video/*"
        hidden
        onChange={handleFileChange}
      />
    </Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>

                <Button
                  id="dropdown2-button"
                  variant="outlined"
                  size="small"
                  onClick={handleMenu2Click}
                  endIcon={<ArrowDropDownIcon />}
                  disabled={loading}
                >
                  {dropdown2.charAt(0).toUpperCase() + dropdown2.slice(1)}
                </Button>
                <Menu
                  anchorEl={anchorEl2}
                  open={Boolean(anchorEl2)}
                  onClose={handleMenu2Close}
                  onClick={handleMenu2Close}
                  slotProps={{
                    paper: {
                      variant: 'outlined',
                      elevation: 0,
                      sx: {
                        my: '4px',
                      },
                    },
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <MenuItem selected={dropdown2 === 'insightful'} onClick={handleDropdown2Change('insightful')}>
                    Insightful
                  </MenuItem>
                  <MenuItem selected={dropdown2 === 'funny'} onClick={handleDropdown2Change('funny')}>
                    Funny
                  </MenuItem>
                  <MenuItem selected={dropdown2 === 'provocative'} onClick={handleDropdown2Change('provocative')}>
                    Provocative
                  </MenuItem>
                  <MenuItem selected={dropdown2 === 'sarcastic'} onClick={handleDropdown2Change('sarcastic')}>
                    Sarcastic
                  </MenuItem>
                </Menu>
              </Box>

              <Button
                type="submit"
                variant="contained"
                disabled={loading || Boolean(error)}
                sx={{
                  ml: 'auto',
                  '&.Mui-disabled': {
                    color: 'grey.200',
                  },
                }}
              >
                {loading ? 'Generating...' : 'Generate ✨'}
              </Button>
            </Box>

            {error && (
              <Typography variant="caption" color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}

            {showAdvanced && (
              <Box sx={{ mb: 2 }}>
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                  <FormLabel>Avoid</FormLabel>
                  <FormGroup row>
                    {allFeatures.map((feature) => (
                      <FormControlLabel
                        key={feature}
                        control={
                          <Checkbox
                            name={feature}
                            checked={selectedFeatures.includes(feature)}
                            onChange={handleFeatureChange}
                            disabled={loading}
                          />
                        }
                        label={feature.charAt(0).toUpperCase() + feature.slice(1)}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Format</FormLabel>
                  <FormGroup row>
                    {allFormats.map((format) => (
                      <FormControlLabel
                        key={format}
                        control={
                          <Checkbox
                            name={format}
                            checked={selectedFormats.includes(format)}
                            onChange={handleFormatChange}
                            disabled={loading}
                          />
                        }
                        label={format.charAt(0).toUpperCase() + format.slice(1)}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Box>
            )}
          </form>
        </Card>
      </Box>
      <Tweets 
        loading={loading} 
        tweets={tweets} 
        tweetsMetadata={tweetsMetadata} 
        progress={progress}
        totalTime={totalTime}
      />
    </Box>
  );
};

export default Generate;
